import { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@hrms/ui';
import { HomeCard } from './HomeCard.js';
import { useAuthenticatedSession } from '../../session/SessionProvider.js';
import { useHolidays, useLeaveRequests } from '../leave/hooks.js';
import { dateOnlyToDate, useToday } from './useToday.js';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** `YYYY-MM-DD` for a UTC-noon date built by `dateOnlyToDate` — pure calendar arithmetic, no zones. */
function toDateOnly(d: Date): string {
  return d.toISOString().slice(0, 10);
}

interface Cell {
  date: string;
  inMonth: boolean;
}

function buildGrid(year: number, month: number): Cell[] {
  const first = new Date(Date.UTC(year, month, 1, 12));
  const start = new Date(first);
  start.setUTCDate(1 - first.getUTCDay());
  const cells: Cell[] = [];
  for (let i = 0; i < 42; i += 1) {
    const d = new Date(start);
    d.setUTCDate(start.getUTCDate() + i);
    cells.push({ date: toDateOnly(d), inMonth: d.getUTCMonth() === month });
  }
  return cells;
}

/**
 * Month view with holiday and leave markers. "Today" comes from the employee's location (see
 * `useToday`), so it never disagrees with the attendance card on a day boundary. Team events have
 * no contract yet, so there is deliberately no Event marker or legend entry.
 */
export function MonthCalendar() {
  // Undefined until the location's date loads: no cell is marked as today before then, rather
  // than guessing. The month shown falls back to the browser's, purely to have a grid to draw.
  const today = useToday();
  const holidaysQuery = useHolidays();

  const [offset, setOffset] = useState(0);
  const anchor = dateOnlyToDate(today ?? new Date().toISOString().slice(0, 10));
  const shown = new Date(Date.UTC(anchor.getUTCFullYear(), anchor.getUTCMonth() + offset, 1, 12));
  const year = shown.getUTCFullYear();
  const month = shown.getUTCMonth();
  const cells = useMemo(() => buildGrid(year, month), [year, month]);

  const { me } = useAuthenticatedSession();
  const leaveQuery = useLeaveRequests();

  const holidayDates = useMemo(
    () =>
      new Set(
        holidaysQuery.data?.status === 200 ? holidaysQuery.data.body.items.map((h) => h.date) : [],
      ),
    [holidaysQuery.data],
  );
  // Every day covered by the caller's own pending or approved leave, expanded from each range.
  const leaveDates = useMemo(() => {
    const dates = new Set<string>();
    if (leaveQuery.data?.status !== 200) return dates;
    for (const request of leaveQuery.data.body.items) {
      if (request.personId !== me.person.id) continue;
      if (request.status !== 'pending' && request.status !== 'approved') continue;
      const day = dateOnlyToDate(request.startDate);
      const end = dateOnlyToDate(request.endDate);
      for (let guard = 0; day <= end && guard < 366; guard += 1) {
        dates.add(toDateOnly(day));
        day.setUTCDate(day.getUTCDate() + 1);
      }
    }
    return dates;
  }, [leaveQuery.data, me.person.id]);

  const title = shown.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });

  return (
    <HomeCard
      title={title}
      action={
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label="Previous month"
            onClick={() => setOffset((o) => o - 1)}
            className="rounded-md p-1 text-fg-subtle hover:bg-bg-subtle hover:text-fg"
          >
            <ChevronLeft className="size-4" />
          </button>
          <button
            type="button"
            aria-label="Next month"
            onClick={() => setOffset((o) => o + 1)}
            className="rounded-md p-1 text-fg-subtle hover:bg-bg-subtle hover:text-fg"
          >
            <ChevronRight className="size-4" />
          </button>
        </div>
      }
    >
      <div className="grid grid-cols-7 gap-y-1 text-center text-xs font-semibold">
        {WEEKDAYS.map((d) => (
          <span key={d} className="pb-1 text-[11px] text-fg-subtle">
            {d}
          </span>
        ))}
        {cells.map((cell) => {
          const dayNum = Number(cell.date.slice(8));
          const isToday = cell.date === today;
          const holiday = holidayDates.has(cell.date);
          const leave = leaveDates.has(cell.date);

          return (
            <div
              key={cell.date}
              className="relative py-0.5 flex flex-col items-center justify-center"
            >
              <span
                className={cn(
                  'flex size-7 items-center justify-center rounded-full text-xs font-semibold tabular-nums',
                  cell.inMonth ? 'text-fg' : 'text-fg-subtle',
                  isToday && 'bg-accent font-bold text-fg-on-accent shadow-xs',
                )}
              >
                {dayNum}
              </span>
              {!isToday && (holiday || leave) && (
                <span
                  className={cn(
                    'absolute bottom-0 size-1 rounded-full',
                    holiday && 'bg-accent',
                    leave && 'bg-warning',
                  )}
                />
              )}
            </div>
          );
        })}
      </div>

      <ul className="mt-3 flex items-center justify-between border-t border-border pt-3 text-[11px] text-fg-muted">
        <LegendItem className="bg-accent" label="Today" />
        <LegendItem className="bg-accent" label="Holiday" />
        <LegendItem className="bg-warning" label="Leave" />
      </ul>
    </HomeCard>
  );
}

function LegendItem({ className, label }: { className: string; label: string }) {
  return (
    <li className="flex items-center gap-1.5">
      <span className={cn('size-2 rounded-full', className)} aria-hidden="true" />
      <span>{label}</span>
    </li>
  );
}
